/**
 * <ActivityDetailsHeader> — la tête de la fiche d'une activité (organism).
 *
 * Le titre, la matière et le chapitre, le statut, et le chrono « 12 / 20 min ». C'est ce que le
 * legacy pose dans `components/ActivityItemInfo.js`, avec deux écarts assumés :
 *  - le titre monte en `<Title>` : sur une fiche, le titre EST l'information, pas un intitulé ;
 *  - le statut porte son mot (`<ActivityStatusBadge>` et non la pastille muette du legacy).
 *
 * À défaut de chapitre, la ligne nomme la matière — comme le legacy.
 *
 * `showTitle={false}` ne garde que la ligne : quand le titre est déjà dans l'en-tête de la fiche,
 * le répéter coûte une ligne de hauteur à la copie.
 */
import React from "react"

import { type UIActivityDetails } from "../../../ports/primary/ui/types"
import { SubjectBadge } from "../../atoms/SubjectBadge"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Icon } from "../../atoms/icons/Icon"
import { Text } from "../../atoms/typography/Text"
import { Title } from "../../atoms/typography/Title"
import { ActivityStatusBadge } from "../../molecules/ActivityStatusBadge"
import { StyleSheet } from "react-native-unistyles"

export type ActivityDetailsHeaderProps = {
    activity: UIActivityDetails
    /** Le titre en tête. Défaut `true` ; `false` quand l'en-tête de la fiche le porte déjà. */
    showTitle?: boolean
    testID?: string
}

export function ActivityDetailsHeader(props: ActivityDetailsHeaderProps) {
    const { activity, showTitle = true, testID } = props

    const { title, subject, chapterName, status, doneCount, estimatedDurationMinutes, spentDurationSeconds } = activity

    const spentMinutes = spentDurationSeconds != null ? Math.floor(spentDurationSeconds / 60) : null
    const durationLabel =
        estimatedDurationMinutes != null
            ? `${spentMinutes != null ? spentMinutes : "-"} / ${estimatedDurationMinutes} min`
            : null

    return (
        <Stack gap="3" testID={testID}>
            {showTitle && <Title size="text-2xl">{title}</Title>}
            <Row gap="2" align="center" style={styles.meta}>
                <SubjectBadge subject={subject} showLabel={chapterName == null} fullLabel />
                {chapterName != null && chapterName !== "" && (
                    <Text size="text-sm" color="text-muted" style={styles.chapter}>
                        {chapterName}
                    </Text>
                )}
                {status != null && <ActivityStatusBadge status={status} count={doneCount} />}
                {durationLabel != null && (
                    <Row gap="1" align="center">
                        <Icon name="stopwatch" size={16} color="text-muted" />
                        <Text size="text-sm" color="text-muted">
                            {durationLabel}
                        </Text>
                    </Row>
                )}
            </Row>
        </Stack>
    )
}

const styles = StyleSheet.create(() => ({
    meta: { flexWrap: "wrap" as const },
    chapter: { flexShrink: 1 },
}))
